<script>
import { mapActions, mapState } from "pinia";

import {
  containerConnexionEmits,
  DEFAULT_ERROR,
  useContainerConnexion
} from "./mixin";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";
import EtatPanier from "@/containers/etat-panier";
import { states } from "@/components/molecules/mixins/form.mixin";
import { typeEtatPanier } from "@/services/panier/panier-helper";
import PopinComptePro from "@/components/organisms/popins-compte/popin-compte-pro";
import PopinActivationServicePrive from "@/components/organisms/popins-compte/popin-activation-service-prive";
import ModifierMotDePasse from "@/containers/mon-compte/mon-profil/modifier-mot-de-passe";
import Popin from "@/containers/popin-adaptive";
import { AuthStates, useAuthStore } from "@/store/auth";
import { useDatalayerStore } from "@/store/datalayer";
import { useClientStore } from "@/store/client";
import { useMagasinStore } from "@/store/magasin";
import { useContextStore } from "@/store/context";

export default {
  components: {
    EtatPanier,
    PopinComptePro,
    PopinActivationServicePrive,
    Popin,
    ModifierMotDePasse
  },
  props: {
    displayCreateAccount: {
      type: Boolean,
      required: false,
      default: true
    },
    pathCreateAccount: {
      type: String,
      required: false,
      default: "/creation-compte"
    },
    popin: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  emits: ["createAccount", ...containerConnexionEmits()],
  setup(props, context) {
    const { trackEvent } = useInteractionDoneTracking();
    const {
      state,
      error,
      layout,
      captchaKey,
      etatPanier,
      magasinId,
      isNational,
      isStoreLocatorVisible,
      isPro,
      isProHeader,
      checkStatus,
      login,
      displayOrCloseStoreLocatorPopin,
      component,
      cannotCheckoutPro,
      magasinHasNoServicePro,
      dispatchConnected
    } = useContainerConnexion(context);

    return {
      trackEvent,
      state,
      error,
      layout,
      captchaKey,
      etatPanier,
      magasinId,
      isNational,
      isStoreLocatorVisible,
      isPro,
      isProHeader,
      checkStatus,
      login,
      displayOrCloseStoreLocatorPopin,
      component,
      cannotCheckoutPro,
      magasinHasNoServicePro,
      dispatchConnected
    };
  },
  data() {
    return {
      displayPopinServicePrive: false,
      needReloadPage: false
    };
  },
  computed: {
    ...mapState(useContextStore, ["isMobile"]),
    ...mapState(useAuthStore, ["authState", "isLogged"]),
    ...mapState(useClientStore, [
      "servicePriveData",
      "client",
      "isNouvelleRegleMdp",
      "isPro"
    ]),
    ...mapState(useMagasinStore, {
      magasin: "magasin",
      oldMagasin: "oldMagasin",
      magasinId: "id",
      isNational: "isNational",
      datePremierCreneauDriveCarrefour: "datePremierCreneauDriveCarrefour"
    }),
    ...mapState(useDatalayerStore, ["authentificatedFrom"]),
    popinVisible() {
      return this.authState === AuthStates.connexion;
    }
  },
  methods: {
    ...mapActions(useAuthStore, {
      setAuthState: "setAuthState",
      resetConnexionCallback: "resetConnexionCallback"
    }),
    ...mapActions(useDatalayerStore, {
      setAuthFrom: "setAuthFrom"
    }),
    ...mapActions(useClientStore, {
      deleteServicePriveData: "deleteServicePriveData",
      handleServicePriveAction: "handleServicePrive"
    }),
    ...mapActions(useMagasinStore, ["fetchMagasin"]),
    async connexion(loginObj) {
      let isStopped = false;
      this.state = states.LOADING;
      this.displayPopinServicePrive = true;
      try {
        await this.login(loginObj);
        if (this.isNational) {
          await this.fetchMagasin(this.client.attributes.id_magasin);
          this.needReloadPage = !!!this.oldMagasin;
        }
        await this.checkStatus({ checkEtatPanier: true });
        this.setAuthState(null);
        this.state = states.SUCCESS;
        if (
          this.etatPanier &&
          [
            typeEtatPanier.RECUPERE,
            typeEtatPanier.COMPLETE_AFTER_CONNECT
          ].includes(this.etatPanier.type)
        ) {
          isStopped = true;
          this.$refs.etatPanierRef.show();
        }
        if (this.servicePriveData) {
          isStopped = true;
        }
        if (this.magasinHasNoServicePro || this.cannotCheckoutPro) {
          isStopped = true;
          this.$refs.popinPro.show();
        }
        if (!this.isNouvelleRegleMdp && !this.isPro) {
          isStopped = true;
          this.$refs.popinModifierMotDePasse.show();
        }
        if (!isStopped) {
          this.dispatchConnected(this.needReloadPage);
        }
        if (this.$route && this.$route.name === "creation-compte") {
          this.$router.replace("/");
        }
        this.trackEvent({
          actionGA: this.authentificatedFrom,
          categoryGA: trackingCodes.CATEGORYGA.loginOk,
          labelGA: ""
        });
      } catch (error) {
        this.error = DEFAULT_ERROR;
        this.trackEvent({
          actionGA: this.authentificatedFrom,
          categoryGA: trackingCodes.CATEGORYGA.loginFail,
          labelGA: ""
        });
        this.state = states.ERROR;
      }
    },
    async createAccount() {
      this.setAuthState(null);
      this.$emit("createAccount");
      navigateToInternal(this.pathCreateAccount);
    },
    hidden() {
      this.error = null;
      this.setAuthFrom("");
      this.setAuthState(null);
    },
    cancel() {
      this.hidden();
      this.resetConnexionCallback();
    },
    forgotPassword() {
      this.setAuthState(null);
    },
    showStoreLocator() {
      this.displayOrCloseStoreLocatorPopin();
      this.$refs.popinPro.hide();
    },
    popinCompteProClosed() {
      if (!this.isStoreLocatorVisible) {
        this.dispatchConnected(this.needReloadPage);
      }
    },
    popinServicePriveClosed() {
      this.displayPopinServicePrive = false;
      this.deleteServicePriveData();
      this.dispatchConnected(this.needReloadPage);
    },
    closePopinModifierMotDePasse() {
      this.$refs.popinModifierMotDePasse.hide();
      this.dispatchConnected(this.needReloadPage);
    }
  },
  mounted() {
    if (this.servicePriveData) {
      this.displayPopinServicePrive = true;
    }
  },
  beforeUnmount() {
    this.deleteServicePriveData();
  },
  watch: {
    isStoreLocatorVisible: {
      handler(value) {
        if (!value) {
          this.dispatchConnected(this.needReloadPage);
        }
      }
    }
  }
};
</script>

<template>
  <div>
    <component
      :is="component"
      ref="connexion"
      :state="state"
      :error="error"
      :popin="popin"
      :popin-visible="popinVisible"
      :display-create-account="displayCreateAccount && !isProHeader"
      :captcha-key="captchaKey"
      @connexion="connexion"
      @createAccount="createAccount"
      @forgotPassword="forgotPassword"
      @cancel="cancel"
      @hidden="hidden"
    />
    <EtatPanier
      ref="etatPanierRef"
      @closed="dispatchConnected(needReloadPage)"
      @ignore="dispatchConnected(needReloadPage)"
    />
    <PopinComptePro
      ref="popinPro"
      :magasin="magasin"
      :is-one-link="isMobile"
      :no-service="magasinHasNoServicePro"
      @closed="popinCompteProClosed()"
      @changeMagasin="showStoreLocator()"
    />
    <Popin
      ref="popinModifierMotDePasse"
      aria-label="Modifier le mot de passe"
      :back-drop-close="false"
      :show-close-button="false"
      page
    >
      <ModifierMotDePasse
        is-obsolete
        @updated="closePopinModifierMotDePasse"
      />
    </Popin>
    <PopinActivationServicePrive
      v-if="displayPopinServicePrive && servicePriveData"
      :visible="servicePriveData !== null"
      :nom-retrait="servicePriveData.nom_retrait"
      :nom-magasin="servicePriveData.nom_magasin"
      @closed="popinServicePriveClosed()"
    />
  </div>
</template>
